import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { Drawer } from "@material-ui/core";
import { ValueTypes } from "pages/refiAnalysis/supervised/constants";
import { prepaymentTypes } from "constants/prepaymentTypes";
import InterestRateTypes, { interestRateTypeList } from "constants/interestRateTypes";
import { getSponsorCompanies } from "store/state/companies";
import { getConfigItemsByParents } from "store/state/configItems";
import { getPrepaymentTypes, getLoanTypes } from "store/state/enumeratedTypes";
import { STATES, ASSET_TYPE } from "constants/configItemParents";
import numeral from "numeral";
import { isAfter } from "date-fns";

import {
  filterScreenerData,
  getSavedDealScreenerFilters,
  saveNewDealScreenerFilter,
  updateDealScreenerFilterPreset,
  deleteDealScreenerFilterPreset
} from "../DealScreener.store";

import "./filterPanelStyles.css";
import moment from "moment";

import {
  FPHeader,
  CollapsibleFilter,
  CollapsibleFilterSection,
  DropdownFilterDrawer,
  MinMaxInputFilter,
  OptionsFilter,
  SingleInputFilter,
  DropdownPresetsDrawer
} from "./";

export const RateFilterType = {
  NO_PREFERENCE: "No Preference",
  FIXED: "Fixed",
  FLOATING: "Floating"
};

export const LockoutType = {
  NO_PREFERENCE: "No Preference",
  YES: "Yes",
  NO: "No"
};

export const NOIType = {
  NO_PREFERENCE: "No Preference",
  PLUS_FIVE: "NOI + 5%",
  MINUS_FIVE: "NOI - 5%"
};

const FilterPanel = ({
  dealScreener,
  clearFiltersFlag,
  onClearFilters: handleOnClearFiltersClicked,
  filterPanelIsOpen,
  onClose,
  getSponsorCompanies,
  companies,
  authentication,
  filterScreenerData,
  getConfigItemsByParents,
  filterConfigItems,
  getPrepaymentTypes,
  getLoanTypes,
  enumeratedTypes,
  getSavedDealScreenerFilters,
  savedFilters,
  saveNewDealScreenerFilter,
  newSavedFilter,
  updateDealScreenerFilterPreset,
  deleteDealScreenerFilterPreset,
  setEmailCampaignFilter,
  isEmailCampaign
}) => {
  const CURRENT_DATE = new Date();

  // #region State

  // #region Presets
  const [presets, setPresets] = useState(savedFilters);
  const [dropdownPresetsVisibility, setDropdownPresetsVisibility] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState({ id: "", name: "", body: {} });
  const [saveNewPresetIsSelected, setSaveNewPresetIsSelected] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  // #endregion Presets

  // #region Existing Loan Criteria
  const [existingLoanCriteriaSectionIsCollapsed, setExistingLoanCriteriaSectionIsCollapsed] = useState(true);

  // Min/Max Loan Amount
  const [minLoanAmountError, setMinLoanAmountError] = useState(false);
  const [maxLoanAmountError, setMaxLoanAmountError] = useState(false);
  const [minLoanAmount, setMinLoanAmount] = useState(0);
  const [maxLoanAmount, setMaxLoanAmount] = useState(0);
  const [minLoanAmountDefault, setMinLoanAmountDefault] = useState(0);
  const [maxLoanAmountDefault, setMaxLoanAmountDefault] = useState(0);
  const [loanAmountIsActive, setLoanAmountIsActive] = useState(false);

  // Rate Type
  const [selectedRateType, setSelectedRateType] = useState(RateFilterType.NO_PREFERENCE);
  const [selectedRateTypeIsActive, setSelectedRateTypeIsActive] = useState(false);

  const rateTypeOptions = [
    { name: RateFilterType.NO_PREFERENCE, index: 0, values: [InterestRateTypes.FIXED, InterestRateTypes.FLOATING] },
    { name: RateFilterType.FIXED, index: 1, values: [InterestRateTypes.FIXED] },
    { name: RateFilterType.FLOATING, index: 2, values: [InterestRateTypes.FLOATING] }
  ];

  // Min/Max All-in Rate
  const [minAllInRateError, setMinAllInRateError] = useState(false);
  const [maxAllInRateError, setMaxAllInRateError] = useState(false);
  const [minAllInRate, setMinAllInRate] = useState(0);
  const [maxAllInRate, setMaxAllInRate] = useState(0);
  const [minAllInRateDefault, setMinAllInRateDefault] = useState(0);
  const [maxAllInRateDefault, setMaxAllInRateDefault] = useState(0);
  const [allInRateActive, setAllInRateIsActive] = useState(false);

  // Spread
  const [minSpreadError, setMinSpreadError] = useState(false);
  const [maxSpreadError, setMaxSpreadError] = useState(false);
  const [minSpread, setMinSpread] = useState(0);
  const [maxSpread, setMaxSpread] = useState(0);
  const [minSpreadDefault, setMinSpreadDefault] = useState(0);
  const [maxSpreadDefault, setMaxSpreadDefault] = useState(0);
  const [spreadIsActive, setSpreadIsActive] = useState(false);

  // IO End Date Within
  const [ioEndDateWithinMonths, setIoEndDateWithinMonths] = useState(20);
  const [ioEndDateWithinMonthsError, setIoEndDateWithinMonthsError] = useState(false);
  const [ioEndDateWithinMonthsDefault, setIoEndDateWithinMonthsDefault] = useState(20);
  const [ioEndDatewithinMonthsIsActive, setIoEndDateWithinMonthsIsActive] = useState(false);

  // Maturity Date Within
  const [maturityDateWithinMonths, setMaturityDateWithinMonths] = useState(20);
  const [maturityDateWithinMonthsError, setMaturityDateWithinMonthsError] = useState(false);
  const [maturityDateWithinMonthsDefault, setMaturityDateWithinMonthsDefault] = useState(20);
  const [maturityDateWithinMonthsDefaultIsActive, setMaturityDateWithinMonthsDefaultIsActive] = useState(false);

  // Prepayent Types
  const [allPrepaymentTypes, setAllPrepaymentTypes] = useState([]);
  const [dropdownFilterPrepaymentTypesVisibility, setDropdownFilterPrepaymentTypesVisibility] = useState(false);
  const [selectedPrepaymentTypes, setSelectedPrepaymentTypes] = useState([]);

  // Prepayment Percentage
  const [minPrepaymentPctError, setMinPrepaymentPctError] = useState(false);
  const [maxPrepaymentPctError, setMaxPrepaymentPctError] = useState(false);
  const [minPrepaymentPct, setMinPrepaymentPct] = useState(0);
  const [maxPrepaymentPct, setMaxPrepaymentPct] = useState(0);
  const [minPrepaymentPctDefault, setMinPrepaymentPctDefault] = useState(0);
  const [maxPrepaymentPctDefault, setMaxPrepaymentPctDefault] = useState(0);
  const [prepaymentPctIsActive, setPrepaymentPctIsActive] = useState(false);

  // Lockout
  const [selectedLockout, setSelectedLockout] = useState(LockoutType.NO_PREFERENCE);
  const [selectedLockoutIsActive, setSelectedLockoutIsActive] = useState(false);

  const lockoutOptions = [
    { name: LockoutType.NO_PREFERENCE, index: 0, values: [0, 1] },
    { name: LockoutType.YES, index: 1, values: [1] },
    { name: LockoutType.NO, index: 2, values: [0] }
  ];

  // Lockout End Date Within
  const [lockoutEndDateWithinMonths, setLockoutEndDateWithinMonths] = useState(1);
  const [lockoutEndDateWithinMonthsError, setLockoutEndDateWithinMonthsError] = useState(false);
  const [lockoutEndDateWithinMonthsDefault, setLockoutEndDateWithinMonthsDefault] = useState(1);
  const [lockoutEndDateIsActive, setLockoutEndDateIsActive] = useState(false);

  // Prepayment Date Within
  const [prepaymentEndDateWithinMonths, setPrepaymentEndDateWithinMonths] = useState(20);
  const [prepaymentEndDateWithinMonthsError, setPrepaymentEndDateWithinMonthsError] = useState(false);
  const [prepaymentEndDateWithinMonthsDefault, setPrepaymentEndDateWithinMonthsDefault] = useState(1);
  const [prepaymentEndDateWithinMonthsActive, setPrepaymentEndDateWithinMonthsActive] = useState(false);
  // #endregion

  // #region Sponsor Companies
  const [allPossibleSponsorCompanies, setAllPossibleSponsorCompanies] = useState([]);
  const [dropdownFilterSponsorCompaniesVisibility, setDropdownFilterSponsorCompaniesVisibility] = useState(false);
  const [allSponsorCompanies, setAllSponsorCompanies] = useState([]);
  const [selectedSponsorCompanies, setSelectedSponsorCompanies] = useState([]);
  // #endregion Sponsor Companies

  // #region Property Criteria
  const [propertyCriteriaSectionIsCollapsed, setPropertyCriteriaSectionIsCollapsed] = useState(true);

  const [allStates, setAllStates] = useState([]);
  const [dropdownFilterStatesVisibility, setDropdownFilterStatesVisibility] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);

  const [allPropertyTypes, setAllPropertyTypes] = useState([]);
  const [dropdownFilterPropertyTypesVisibility, setDropdownFilterPropertyTypesVisibility] = useState(false);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState([]);
  // #endregion Property Criteria

  const [saveIsEnabled, setSaveIsEnabled] = useState(false);
  // #endregion State

  const dsLoanAmounts = dealScreener?.map(deal => deal?.currentDeal?.loanAmount).filter(amount => amount !== undefined);
  const dsAllInRates = dealScreener?.filter(deal => deal?.currentDeal?.rate !== undefined)?.map(deal => deal?.currentDeal?.rate * 100);
  const dsSpreadRates = dealScreener?.filter(deal => deal?.currentDeal?.spread)?.map(deal => deal?.currentDeal?.spread * 100);
  const dsMaturityDate = dealScreener?.filter(deal => deal?.currentDeal?.maturity !== undefined)?.map(deal => deal?.currentDeal?.maturity);
  const dsLockoutEndDates = dealScreener?.filter(deal => {
    const date = deal?.currentDeal?.prepaymentPenalty?.lockoutEndDate;

    return date && isAfter(new Date(date), CURRENT_DATE);
  }).map(deal => deal?.currentDeal?.prepaymentPenalty?.lockoutEndDate);

  const dsPrepaymentDates = dealScreener?.filter(deal => {
    const date = deal?.currentDeal?.prepaymentPenalty?.prepaymentDate;

    return date && isAfter(new Date(date), CURRENT_DATE);
  }).map(deal => deal?.currentDeal?.prepaymentPenalty?.prepaymentDate);

  const dsIoEndDates = dealScreener?.filter(deal => {
    const date = deal?.currentDeal?.ioEndDate;

    return date && isAfter(new Date(date), CURRENT_DATE);
  }).map(deal => deal?.currentDeal?.ioEndDate);

  const dsPrepaymentPenaltyPct = dealScreener?.filter(deal => deal?.currentDeal?.prepaymentPenalty?.percentOfLoanAmount !== undefined)?.map(deal => deal?.currentDeal?.prepaymentPenalty?.percentOfLoanAmount * 100);
  // Handles when save is enabled
  const noErrors =
    !minLoanAmountError &&
    !maxLoanAmountError &&
    !minAllInRateError &&
    !maxAllInRateError &&
    !minSpreadError &&
    !maxSpreadError &&
    !ioEndDateWithinMonthsError &&
    !maturityDateWithinMonthsError &&
    !minPrepaymentPctError &&
    !maxPrepaymentPctError &&
    !lockoutEndDateWithinMonthsError &&
    !prepaymentEndDateWithinMonthsError;

  const allFilters = [
    minLoanAmount,
    maxLoanAmount,
    minAllInRate,
    maxAllInRate,
    minSpread,
    maxSpread,
    selectedRateType,
    selectedLockout,
    ioEndDateWithinMonths,
    maturityDateWithinMonths,
    minPrepaymentPct,
    maxPrepaymentPct,
    lockoutEndDateWithinMonths,
    prepaymentEndDateWithinMonths,
    selectedSponsorCompanies,
    selectedStates,
    selectedPropertyTypes,
    selectedPrepaymentTypes
  ];

  const allFilterErrors = [
    minLoanAmountError,
    maxLoanAmountError,
    minAllInRateError,
    maxAllInRateError,
    minSpreadError,
    maxSpreadError,
    ioEndDateWithinMonthsError,
    maturityDateWithinMonthsError,
    minPrepaymentPctError,
    maxPrepaymentPctError,
    lockoutEndDateWithinMonthsError,
    prepaymentEndDateWithinMonthsError
  ];

  // #region useEffects
  useEffect(() => {
    getSponsorCompanies();
    getConfigItemsByParents({ parentIds: [STATES, ASSET_TYPE], subKey: "filterPanelConfigItems" });
    getPrepaymentTypes();
    getLoanTypes();
    getSavedDealScreenerFilters();
  }, []);

  useEffect(() => {
    setMinMaxValues();
  }, [dealScreener]);

  useEffect(() => {
    resetState();
  }, [clearFiltersFlag]);

  useEffect(() => {
    setPresets(savedFilters);
  }, [savedFilters]);

  useEffect(() => {
    getSavedDealScreenerFilters();
  }, [newSavedFilter]);

  useEffect(() => {
    const { companySponsorCompanies = [] } = companies;
    setAllPossibleSponsorCompanies(companySponsorCompanies);
  }, [authentication]);

  useEffect(() => {
    setAllStates(filterConfigItems[STATES]);
    setAllPropertyTypes(filterConfigItems[ASSET_TYPE]);
  }, [filterConfigItems]);

  useEffect(() => {
    filterDeals();
  }, [
    ioEndDatewithinMonthsIsActive,
    lockoutEndDateIsActive,
    prepaymentEndDateWithinMonthsActive,
    prepaymentPctIsActive,
    maturityDateWithinMonthsDefaultIsActive,
    loanAmountIsActive,
    allInRateActive,
    spreadIsActive,
    selectedRateTypeIsActive,
    selectedLockoutIsActive,
    ...allFilters
  ]);

  useEffect(() => {
    setAllStates(allStates?.sort((a, b) => a.name.localeCompare(b.name)));
    setAllPropertyTypes(allPropertyTypes?.sort((a, b) => a.name.localeCompare(b.name)));
  }, [allStates, allPropertyTypes]);

  useEffect(() => {
    setAllSponsorCompanies(allPossibleSponsorCompanies?.sort((a, b) => a.name.localeCompare(b.name)));
  }, [allPossibleSponsorCompanies]);

  useEffect(() => {
    const formattedPrepaymentTypes = enumeratedTypes?.prepayment_types?.map(ppt => ({ ...ppt, name: ppt?.value }));
    setAllPrepaymentTypes(formattedPrepaymentTypes?.sort((a, b) => a.name.localeCompare(b.name)));
  }, [enumeratedTypes]);

  useEffect(() => {
    const amountsNotEqualToDefaults =
      minLoanAmount > minLoanAmountDefault ||
      maxLoanAmount < maxLoanAmountDefault ||
      minAllInRate > minAllInRateDefault ||
      maxAllInRate < maxAllInRateDefault ||
      minSpread > minSpreadDefault ||
      maxSpread < maxSpreadDefault ||
      selectedRateType !== RateFilterType.NO_PREFERENCE ||
      selectedLockout !== LockoutType.NO_PREFERENCE ||
      ioEndDateWithinMonths !== ioEndDateWithinMonthsDefault ||
      maturityDateWithinMonths !== maturityDateWithinMonthsDefault ||
      selectedPrepaymentTypes?.length !== 0 ||
      minPrepaymentPct > minPrepaymentPctDefault ||
      maxPrepaymentPct < maxPrepaymentPctDefault ||
      lockoutEndDateWithinMonths !== lockoutEndDateWithinMonthsDefault ||
      prepaymentEndDateWithinMonths !== prepaymentEndDateWithinMonthsDefault ||
      selectedSponsorCompanies?.length !== 0 ||
      selectedStates?.length !== 0 ||
      selectedPropertyTypes?.length !== 0;

    setSaveIsEnabled(amountsNotEqualToDefaults && noErrors);
  }, [...allFilters, ...allFilterErrors]);

  useEffect(() => {
    const { body: selectedPresetBody = {} } = selectedPreset;
    if (Object.keys(selectedPresetBody).length !== 0) {
      setPropertyCriteriaSectionIsCollapsed(false);
      setExistingLoanCriteriaSectionIsCollapsed(false);
      resetStateWithoutPreset();
    }

    if (selectedPresetBody?.loanAmount) {
      setLoanAmountIsActive(true);
      const selectedLoanAmounts = [selectedPresetBody?.loanAmount?.min, selectedPresetBody?.loanAmount?.max];
      setScreenerMinMaxValues(selectedLoanAmounts, setMinLoanAmount, setMaxLoanAmount);
    }

    if (selectedPresetBody?.rateTypes) {
      const rateTypeName = interestRateTypeList.find(rt => rt.value === selectedPresetBody.rateTypes[0]).name;
      setSelectedRateTypeIsActive(true);
      setSelectedRateType(rateTypeName);
    }

    if (selectedPresetBody?.ioEndDateWithinMonths) {
      setIoEndDateWithinMonthsIsActive(true);
      setIoEndDateWithinMonths(selectedPresetBody?.ioEndDateWithinMonths);
    }

    if (selectedPresetBody?.prepaymentEndDateWithinMonths) {
      setPrepaymentEndDateWithinMonthsActive(true);
      setPrepaymentEndDateWithinMonths(selectedPresetBody?.prepaymentEndDateWithinMonths);
    }

    if (selectedPresetBody?.maturityDateWithinMonths) {
      setMaturityDateWithinMonthsDefaultIsActive(true);
      setMaturityDateWithinMonths(selectedPresetBody?.maturityDateWithinMonths);
    }

    if (selectedPresetBody?.sponsorCompanies) {
      const presetSponsorCompanies = selectedPresetBody?.sponsorCompanies.map(company => allSponsorCompanies.find(c => c.id === company));
      setSelectedSponsorCompanies(presetSponsorCompanies);
    }

    if (selectedPresetBody?.states) {
      const presetStates = selectedPresetBody?.states.map(state => allStates.find(s => s.id === state));
      setSelectedStates(presetStates);
    }

    if (selectedPresetBody?.propertyTypes) {
      const presetPropertyTypes = selectedPresetBody?.propertyTypes.map(pt => allPropertyTypes.find(property => property.id === pt));
      setSelectedPropertyTypes(presetPropertyTypes);
    }

    if (selectedPresetBody?.prepaymentTypes) {
      const presetPrepaymentTypes = selectedPresetBody?.prepaymentTypes
        .map(option => prepaymentTypes.find(ppt => ppt.value === option))
        .map(({ value, name, sortOrder }) => ({
          id: value,
          value: name,
          sortOrder: sortOrder * 10,
          __modelName__: "prepaymentType",
          name
        }));
      setSelectedPrepaymentTypes(presetPrepaymentTypes);
    }

    if (selectedPresetBody?.prepaymentPercent) {
      setPrepaymentPctIsActive(true);
      const selectedPrepaymentPercentAmounts = [selectedPresetBody?.prepaymentPercent?.min * 100, selectedPresetBody?.prepaymentPercent?.max * 100];
      setScreenerMinMaxValues(selectedPrepaymentPercentAmounts, setMinPrepaymentPct, setMaxPrepaymentPct);
    }

    if (selectedPresetBody?.allInRate) {
      setAllInRateIsActive(true);
      const selectedAllInRates = [selectedPresetBody?.allInRate?.min * 100, selectedPresetBody?.allInRate?.max * 100];
      setScreenerMinMaxValues(selectedAllInRates, setMinAllInRate, setMaxAllInRate);
    }

    if (selectedPresetBody?.spread) {
      setSpreadIsActive(true);
      const presetSpreads = [selectedPresetBody?.spread?.min * 100, selectedPresetBody?.spread?.max * 100];
      setScreenerMinMaxValues(presetSpreads, setMinSpread, setMaxSpread);
    }

    if (selectedPresetBody?.lockoutEndDateWithinMonths) {
      setLockoutEndDateIsActive(true);
      setLockoutEndDateWithinMonths(selectedPresetBody?.lockoutEndDateWithinMonths);
    }

    if (selectedPresetBody?.preferredLockout !== null && selectedPresetBody?.preferredLockout !== undefined) {
      setSelectedLockoutIsActive(true);
      const { preferredLockout } = selectedPresetBody;
      setSelectedLockout(preferredLockout ? LockoutType.YES : LockoutType.NO);
    }

    if (selectedPresetBody?.prepaymentEndDateWithinMonths) {
      setPrepaymentEndDateWithinMonths(selectedPresetBody?.prepaymentEndDateWithinMonths);
    }
  }, [selectedPreset]);

  // #endregion

  const determineMinMaxValues = arrOfValues => {
    const min = Math.min(...arrOfValues) ?? 0;
    const max = Math.max(...arrOfValues) ?? 0;

    return {
      min: Number(min.toFixed(2)),
      max: Number(max.toFixed(2))
    };
  };

  const setScreenerMinMaxValues = (
    arrOfValues,
    setMinAmount,
    setMaxAmount,
    isDefault = false,
    setMinAmountDefault = () => { },
    setMaxAmountDefault = () => { }
  ) => {
    const { min, max } = determineMinMaxValues(arrOfValues);

    setMinAmount(min);
    setMaxAmount(max);
    if (isDefault) {
      setMinAmountDefault(min);
      setMaxAmountDefault(max);
    }
  };

  const calculateDifferenceInMonths = (futureDateArr, setMaxState, setMaxStateDefault) => {
    const differenceInMonthsArr = futureDateArr.map(futureDate => {
      const formattedFutureDate = moment(futureDate);
      const today = moment();

      return formattedFutureDate.diff(today, "months", true);
    });
    const maxDifference = differenceInMonthsArr.length > 0 ? Math.ceil(Math.max(...differenceInMonthsArr)) : 1;
    setMaxState(maxDifference);
    setMaxStateDefault(maxDifference);
  };

  // #region Dropdown Filters
  const dropdownFilterDrawerIsVisible =
    dropdownPresetsVisibility ||
    dropdownFilterSponsorCompaniesVisibility ||
    dropdownFilterStatesVisibility ||
    dropdownFilterPropertyTypesVisibility ||
    dropdownFilterPrepaymentTypesVisibility;

  const handleOnCheck = (selectedOptionId, isChecked, selectedOption, selectedOptionsState, setSelectedOptionsState) => {
    const newSelectedOptionsState = isChecked ? selectedOptionsState.filter(option => option?.id !== selectedOptionId) : [...selectedOptionsState, { ...selectedOption }];
    setSelectedOptionsState(newSelectedOptionsState);
  };
  // #endregion

  const percentErrorMsgFormat = amount => `${numeral(amount).format("0.00%")}`;
  const currencyErrorMsgFormat = amount => `${numeral(amount).format("$0,0")}`;

  const setMinMaxValues = () => {
    setScreenerMinMaxValues(dsLoanAmounts, setMinLoanAmount, setMaxLoanAmount, true, setMinLoanAmountDefault, setMaxLoanAmountDefault);
    setScreenerMinMaxValues(dsAllInRates, setMinAllInRate, setMaxAllInRate, true, setMinAllInRateDefault, setMaxAllInRateDefault);
    setScreenerMinMaxValues(dsSpreadRates, setMinSpread, setMaxSpread, true, setMinSpreadDefault, setMaxSpreadDefault);
    calculateDifferenceInMonths(dsMaturityDate, setMaturityDateWithinMonths, setMaturityDateWithinMonthsDefault);
    setScreenerMinMaxValues(dsPrepaymentPenaltyPct, setMinPrepaymentPct, setMaxPrepaymentPct, true, setMinPrepaymentPctDefault, setMaxPrepaymentPctDefault);
    calculateDifferenceInMonths(dsLockoutEndDates, setLockoutEndDateWithinMonths, setLockoutEndDateWithinMonthsDefault);
    calculateDifferenceInMonths(dsPrepaymentDates, setPrepaymentEndDateWithinMonths, setPrepaymentEndDateWithinMonthsDefault);
    calculateDifferenceInMonths(dsIoEndDates, setIoEndDateWithinMonths, setIoEndDateWithinMonthsDefault);
  };

  const resetStateWithoutPreset = () => {
    setMinMaxValues();
    setSelectedRateType(RateFilterType.NO_PREFERENCE);
    setSelectedLockout(LockoutType.NO_PREFERENCE);
    setSelectedPrepaymentTypes([]);
    setSelectedSponsorCompanies([]);
    setSelectedStates([]);
    setSelectedPropertyTypes([]);
  };

  const setSelectedPresetForScreener = preset => {
    setSelectedPreset(preset);
    if (isEmailCampaign) {
      setEmailCampaignFilter(preset);
    }
  };

  const resetState = () => {
    resetStateWithoutPreset();
    resetActiveFlags();
    setSelectedPreset({ id: "", name: "", body: {} });
  };

  const resetActiveFlags = () => {
    setSpreadIsActive(false);
    setLoanAmountIsActive(false);
    setAllInRateIsActive(false);
    setLockoutEndDateIsActive(false);
    setIoEndDateWithinMonthsIsActive(false);
    setPrepaymentEndDateWithinMonthsActive(false);
    setMaturityDateWithinMonthsDefaultIsActive(false);
    setPrepaymentPctIsActive(false);
    setSelectedRateTypeIsActive(false);
    setSelectedLockoutIsActive(false);
  };

  const payloadToFilter = () => {
    const filterPayload = {};

    if (spreadIsActive) {
      filterPayload.spread = {
        min: minSpread / 100,
        max: maxSpread / 100
      };
    }

    if (loanAmountIsActive) {
      filterPayload.loanAmount = {
        min: minLoanAmount,
        max: maxLoanAmount
      };
    }

    if (allInRateActive) {
      filterPayload.allInRate = {
        min: minAllInRate / 100,
        max: maxAllInRate / 100
      };
    }

    if (lockoutEndDateIsActive) {
      filterPayload.lockoutEndDateWithinMonths = lockoutEndDateWithinMonths;
    }

    if (ioEndDatewithinMonthsIsActive) {
      filterPayload.ioEndDateWithinMonths = ioEndDateWithinMonths;
    }

    if (prepaymentEndDateWithinMonthsActive) {
      filterPayload.prepaymentEndDateWithinMonths = prepaymentEndDateWithinMonths;
    }

    if (maturityDateWithinMonthsDefaultIsActive) {
      filterPayload.maturityDateWithinMonths = maturityDateWithinMonths;
    }

    if (prepaymentPctIsActive) {
      filterPayload.prepaymentPercent = {
        min: minPrepaymentPct / 100,
        max: maxPrepaymentPct / 100
      };
    }

    if (selectedPrepaymentTypes.length > 0) {
      filterPayload.prepaymentTypes = selectedPrepaymentTypes.map(({ id }) => id);
    }

    if (selectedPropertyTypes.length > 0) {
      filterPayload.propertyTypes = selectedPropertyTypes.map(({ id }) => id);
    }

    if (selectedSponsorCompanies.length > 0) {
      filterPayload.sponsorCompanies = selectedSponsorCompanies.map(({ id }) => id);
    }

    if (selectedStates.length > 0) {
      filterPayload.states = selectedStates.map(({ id }) => id);
    }

    if (selectedRateType !== RateFilterType.NO_PREFERENCE) {
      const rateTypeId = interestRateTypeList.filter(rateType => rateType.name === selectedRateType)[0].value;
      filterPayload.rateTypes = [rateTypeId];
    }

    if (selectedLockout !== LockoutType.NO_PREFERENCE) {
      filterPayload.preferredLockout = selectedLockout === LockoutType.YES;
    }

    return filterPayload;
  };

  const filterDeals = () => filterScreenerData(payloadToFilter());
  const saveNewPreset = () => saveNewDealScreenerFilter({ name: newPresetName, body: payloadToFilter() });
  const updateSelectedPreset = () => {
    const newSavedFilters = savedFilters.map(filter => (filter.id === selectedPreset.id ?
      { name: selectedPreset.name, ...selectedPreset } :
      { ...filter }));
    updateDealScreenerFilterPreset({ name: selectedPreset?.name, id: selectedPreset?.id });
    setPresets(newSavedFilters);
  };

  const monthLabel = numOfMonths => (numOfMonths === 1 ? "Month" : "Months");

  return (
    <Drawer className="dl-scrn-filter-panel-container" open={filterPanelIsOpen} onClose={onClose} anchor="right">
      {filterPanelIsOpen && dropdownPresetsVisibility &&
        <DropdownPresetsDrawer
          presets={presets}
          setPresets={setPresets}
          saveNewPresetIsSelected={saveNewPresetIsSelected}
          onCloseSaveNewPresets={() => setSaveNewPresetIsSelected(false)}
          onClosePresets={() => setDropdownPresetsVisibility(false)}
          saveNewPreset={saveNewPreset}
          newPresetName={newPresetName}
          setNewPresetName={setNewPresetName}
          updateSelectedPreset={updateSelectedPreset}
          deleteDealScreenerFilterPreset={deleteDealScreenerFilterPreset}
          selectedPreset={selectedPreset}
          setSelectedPreset={setSelectedPresetForScreener}
        />}
      {filterPanelIsOpen && dropdownFilterSponsorCompaniesVisibility &&
        <DropdownFilterDrawer
          name="Client"
          allOptions={allSponsorCompanies}
          selectedOptions={selectedSponsorCompanies}
          setSelectedOptions={setSelectedSponsorCompanies}
          setVisibility={setDropdownFilterSponsorCompaniesVisibility}
          handleOnCheck={handleOnCheck}
        />}
      {filterPanelIsOpen && dropdownFilterStatesVisibility &&
        <DropdownFilterDrawer
          name="States"
          allOptions={allStates}
          selectedOptions={selectedStates}
          setSelectedOptions={setSelectedStates}
          setVisibility={setDropdownFilterStatesVisibility}
          handleOnCheck={handleOnCheck}
        />}
      {filterPanelIsOpen && dropdownFilterPropertyTypesVisibility &&
        <DropdownFilterDrawer
          name="Property Types"
          allOptions={allPropertyTypes}
          selectedOptions={selectedPropertyTypes}
          setSelectedOptions={setSelectedPropertyTypes}
          setVisibility={setDropdownFilterPropertyTypesVisibility}
          handleOnCheck={handleOnCheck}
        />}
      {filterPanelIsOpen && dropdownFilterPrepaymentTypesVisibility &&
        <DropdownFilterDrawer
          name="Prepayment Type"
          allOptions={allPrepaymentTypes}
          selectedOptions={selectedPrepaymentTypes}
          setSelectedOptions={setSelectedPrepaymentTypes}
          setVisibility={setDropdownFilterPrepaymentTypesVisibility}
          handleOnCheck={handleOnCheck}
        />}
      {filterPanelIsOpen && !dropdownFilterDrawerIsVisible &&
        <>
          <FPHeader
            presets={presets}
            filterPanelSaveAndClearAreEnabled={saveIsEnabled}
            onClose={onClose}
            onClearFilters={handleOnClearFiltersClicked}
            onSave={() => {
              setSaveNewPresetIsSelected(true);
              setDropdownPresetsVisibility(true);
            }}
            onShowPresets={() => {
              setDropdownPresetsVisibility(true);
            }}
          />
          <div className="dl-scrn-filter-panel-section-wrapper">
            <CollapsibleFilter
              name="Clients"
              optionCount={allSponsorCompanies?.length}
              toggleDropdownFilterVisibility={() => setDropdownFilterSponsorCompaniesVisibility(true)}
            />
          </div>
          <CollapsibleFilterSection
            title="Property Criteria"
            isCollapsed={propertyCriteriaSectionIsCollapsed}
            setIsCollapsed={setPropertyCriteriaSectionIsCollapsed}
            numberOfFilters={2}
          >
            <CollapsibleFilter
              name="States"
              optionCount={allStates?.length}
              toggleDropdownFilterVisibility={() => setDropdownFilterStatesVisibility(true)}
            />
            <CollapsibleFilter
              name="Property Types"
              optionCount={allPropertyTypes?.length}
              toggleDropdownFilterVisibility={() => setDropdownFilterPropertyTypesVisibility(true)}
            />
          </CollapsibleFilterSection>
          <CollapsibleFilterSection
            title="Existing Loan Criteria"
            isCollapsed={existingLoanCriteriaSectionIsCollapsed}
            setIsCollapsed={setExistingLoanCriteriaSectionIsCollapsed}
            numberOfFilters={11}
          >
            <SingleInputFilter
              title="Maturity Date Within"
              valueType={ValueTypes.integer}
              label={monthLabel(maturityDateWithinMonths)}
              value={maturityDateWithinMonths}
              defaultValue={maturityDateWithinMonthsDefault}
              onBlur={() => {
                !maturityDateWithinMonthsDefaultIsActive ?
                  setMaturityDateWithinMonthsDefaultIsActive(true) :
                  filterDeals();
              }}
              setIsActive={setMaturityDateWithinMonthsDefaultIsActive}
              setAmount={setMaturityDateWithinMonths}
              error={maturityDateWithinMonthsError}
              setError={setMaturityDateWithinMonthsError}
              errorMessage={`The last Maturity Date is within ${maturityDateWithinMonthsDefault} ${maturityDateWithinMonthsDefault === 1 ? "month" : "months"}.`}
            />
            <MinMaxInputFilter
              title="Prepayment Percentage"
              valueType={ValueTypes.percent}
              minError={minPrepaymentPctError}
              setMinError={setMinPrepaymentPctError}
              minLabel="Min Prepayment %"
              minErrorMessage={`Min prepayment percentage is ${percentErrorMsgFormat(minPrepaymentPctDefault / 100)}.`}
              minAmount={minPrepaymentPct}
              minAmountDefault={minPrepaymentPctDefault}
              setMinAmount={setMinPrepaymentPct}
              maxError={maxPrepaymentPctError}
              setMaxError={setMaxPrepaymentPctError}
              maxLabel="Max Prepayment %"
              maxErrorMessage={`Max prepayment percentage is ${percentErrorMsgFormat(maxPrepaymentPctDefault / 100)}.`}
              maxAmount={maxPrepaymentPct}
              maxAmountDefault={maxPrepaymentPctDefault}
              onBlur={() => {
                !prepaymentPctIsActive ? setPrepaymentPctIsActive(true) : filterDeals();
              }}
              setIsActive={setPrepaymentPctIsActive}
              setMaxAmount={setMaxPrepaymentPct}
            />
            <CollapsibleFilter
              name="Prepayment Type"
              optionCount={allPrepaymentTypes?.length}
              toggleDropdownFilterVisibility={() => setDropdownFilterPrepaymentTypesVisibility(true)}
            />
            <SingleInputFilter
              title="IO End Date Within"
              valueType={ValueTypes.integer}
              label={monthLabel(ioEndDateWithinMonths)}
              value={ioEndDateWithinMonths}
              defaultValue={ioEndDateWithinMonthsDefault}
              setAmount={setIoEndDateWithinMonths}
              error={ioEndDateWithinMonthsError}
              onBlur={() => {
                !ioEndDatewithinMonthsIsActive ? setIoEndDateWithinMonthsIsActive(true) : filterDeals();
              }}
              setIsActive={setIoEndDateWithinMonthsIsActive}
              setError={setIoEndDateWithinMonthsError}
              errorMessage={`The last IO End Date is within ${ioEndDateWithinMonthsDefault} ${ioEndDateWithinMonthsDefault === 1 ? "month" : "months"}.`}
            />
            <SingleInputFilter
              title="Prepayment End Date Within"
              valueType={ValueTypes.integer}
              label={monthLabel(prepaymentEndDateWithinMonths)}
              value={prepaymentEndDateWithinMonths}
              defaultValue={prepaymentEndDateWithinMonthsDefault}
              setAmount={setPrepaymentEndDateWithinMonths}
              error={prepaymentEndDateWithinMonthsError}
              setError={setPrepaymentEndDateWithinMonthsError}
              onBlur={() => {
                setPrepaymentEndDateWithinMonthsActive(true);
              }}
              setIsActive={setPrepaymentEndDateWithinMonthsActive}
              errorMessage={`The last Prepayment Date is within ${prepaymentEndDateWithinMonthsDefault} ${prepaymentEndDateWithinMonthsDefault === 1 ? "month" : "months"}.`}
            />
            <SingleInputFilter
              title="Lockout End Date Within"
              valueType={ValueTypes.integer}
              label={monthLabel(lockoutEndDateWithinMonths)}
              value={lockoutEndDateWithinMonths}
              defaultValue={lockoutEndDateWithinMonthsDefault}
              setAmount={setLockoutEndDateWithinMonths}
              error={lockoutEndDateWithinMonthsError}
              setError={setLockoutEndDateWithinMonthsError}
              onBlur={() => {
                setLockoutEndDateIsActive(true);
              }}
              setIsActive={setLockoutEndDateIsActive}
              errorMessage={`The last Lockout End Date is within ${lockoutEndDateWithinMonthsDefault} ${lockoutEndDateWithinMonthsDefault === 1 ? "month" : "months"}.`}
            />
            <MinMaxInputFilter
              title="All-in Rate"
              valueType={ValueTypes.percent}
              minError={minAllInRateError}
              setMinError={setMinAllInRateError}
              minLabel="Minimum All-in Rate"
              minErrorMessage={`Min all-in rate is ${percentErrorMsgFormat(minAllInRateDefault / 100)}.`}
              minAmount={minAllInRate}
              minAmountDefault={minAllInRateDefault}
              setMinAmount={setMinAllInRate}
              maxError={maxAllInRateError}
              setMaxError={setMaxAllInRateError}
              onBlur={() => {
                setAllInRateIsActive(true);
              }}
              setIsActive={setAllInRateIsActive}
              maxLabel="Maximum All-in Rate"
              maxErrorMessage={`Max all-in rate is ${percentErrorMsgFormat(maxAllInRateDefault / 100)}.`}
              maxAmount={maxAllInRate}
              maxAmountDefault={maxAllInRateDefault}
              setMaxAmount={setMaxAllInRate}
            />
            <MinMaxInputFilter
              title="Spread"
              valueType={ValueTypes.percent}
              minError={minSpreadError}
              setMinError={setMinSpreadError}
              minLabel="Minimum Spread"
              minErrorMessage={`Min spread is ${percentErrorMsgFormat(minSpreadDefault / 100)}.`}
              minAmount={minSpread}
              minAmountDefault={minSpreadDefault}
              setMinAmount={setMinSpread}
              maxError={maxSpreadError}
              setMaxError={setMaxSpreadError}
              onBlur={() => {
                setSpreadIsActive(true);
              }}
              setIsActive={setSpreadIsActive}
              maxLabel="Maximum Spread"
              maxErrorMessage={`Max spread is ${percentErrorMsgFormat(maxSpreadDefault / 100)}.`}
              maxAmount={maxSpread}
              maxAmountDefault={maxSpreadDefault}
              setMaxAmount={setMaxSpread}
            />
            <MinMaxInputFilter
              title="Loan Amount"
              valueType={ValueTypes.currency}
              minError={minLoanAmountError}
              setMinError={setMinLoanAmountError}
              minLabel="Minimum loan amount"
              minErrorMessage={`Min loan amount is ${currencyErrorMsgFormat(minLoanAmountDefault)}.`}
              minAmount={minLoanAmount || 0}
              minAmountDefault={minLoanAmountDefault || 0}
              setMinAmount={setMinLoanAmount}
              maxError={maxLoanAmountError}
              setMaxError={setMaxLoanAmountError}
              maxLabel="Maximum loan amount"
              maxErrorMessage={`Max loan amount is ${currencyErrorMsgFormat(maxLoanAmountDefault)}.`}
              maxAmount={maxLoanAmount || 0}
              maxAmountDefault={maxLoanAmountDefault || 0}
              onBlur={() => {
                setLoanAmountIsActive(true);
              }}
              setMaxAmount={setMaxLoanAmount}
              setIsActive={setLoanAmountIsActive}
            />
            <OptionsFilter
              title="Rate Type"
              options={rateTypeOptions}
              defaultOption={RateFilterType.NO_PREFERENCE}
              selectedOption={selectedRateType}
              setFilter={rateType => setSelectedRateType(rateType)}
            />
            <OptionsFilter
              title="Lockout"
              options={lockoutOptions}
              selectedOption={selectedLockout}
              defaultOption={LockoutType.NO_PREFERENCE}
              setFilter={setSelectedLockout}
            />
          </CollapsibleFilterSection>
        </>}
    </Drawer>
  );
};

const mapStateToProps = ({ authentication, companies, configItems, enumeratedTypes, dealScreenerReducer: ds }) => ({
  authentication,
  companies,
  filterConfigItems: configItems.configItemsByParents?.filterPanelConfigItems || {},
  enumeratedTypes: enumeratedTypes.enumeratedTypes,
  savedFilters: ds.savedFilters,
  newSavedFilter: ds.newSavedFilter
});

export default compose(connect(mapStateToProps, {
  getSponsorCompanies,
  filterScreenerData,
  getConfigItemsByParents,
  getPrepaymentTypes,
  getLoanTypes,
  getSavedDealScreenerFilters,
  saveNewDealScreenerFilter,
  updateDealScreenerFilterPreset,
  deleteDealScreenerFilterPreset
}))(FilterPanel);

